import { Capacitor } from '@capacitor/core';
import { logger } from '@deskbay/shared';
import { globalNavigate } from './navigationRef';
import { supabase } from './supabase/client';
import { storage } from './storage';

/**
 * OneSignal Service — Unified Push Notification Service
 *
 * - Native (iOS/Android): usa `onesignal-cordova-plugin` SDK v5
 * - Web: usa OneSignal Web SDK v16 via CDN
 *
 * Le API pubbliche sono identiche per entrambe le piattaforme.
 */

const ONESIGNAL_CDN_URL = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
const ONESIGNAL_APP_ID = '6dabbce4-4307-448f-965f-d0e67863006b';
const DEBUG = false;

/**
 * Persistito (Keychain/EncryptedSharedPreferences — sopravvive a update e reinstall) quando una
 * lettura del permesso push RIESCE e dice "concesso". Serve a distinguere un rifiuto vero da una
 * lettura del bridge Cordova andata in timeout: al cold start post-update il bridge a volte non
 * risponde affatto (bug noto, vedi bibbia OneSignal §3.0) — senza questo flag, un timeout veniva
 * trattato come "permesso negato" e la riparazione automatica (_enforceNativeSubscription) restava
 * saltata finché l'utente non riapriva l'app una seconda volta.
 */
const PUSH_GRANTED_KEY = 'onesignal_push_granted';

/** Sentinel per distinguere "letto false" da "non sono riuscito a leggere in tempo". */
const READ_TIMEOUT = Symbol('onesignal-read-timeout');

/**
 * Esegue una lettura async del bridge nativo con timeout. Non risolve MAI a `false` per un
 * timeout: risolve al sentinel READ_TIMEOUT, che il chiamante deve trattare come "non so",
 * mai come "negato" — altrimenti si ricade nel bug osservato in produzione (letture transitorie
 * false che facevano saltare il self-heal a ogni aggiornamento dell'app).
 */
function readWithTimeout<T>(promise: Promise<T>, ms = 3000): Promise<T | typeof READ_TIMEOUT> {
    return Promise.race([
        promise,
        new Promise<typeof READ_TIMEOUT>(resolve => setTimeout(() => resolve(READ_TIMEOUT), ms)),
    ]);
}

function dbg(step: string, data?: unknown) {
    if (!DEBUG) return;
    logger.notifications.debug(step, 'oneSignalService', data !== undefined ? { data } : undefined);
}

/** Serializza errori in modo leggibile (evita il {} vuoto) */
function serializeError(e: unknown): unknown {
    if (e instanceof Error) {
        return { name: e.name, message: e.message, stack: e.stack?.split('\n').slice(0, 3).join(' | ') };
    }
    if (typeof e === 'object' && e !== null) {
        try {
            const str = JSON.stringify(e);
            if (str !== '{}') return str;
            return Object.getOwnPropertyNames(e).reduce((acc: Record<string, unknown>, key) => {
                acc[key] = (e as Record<string, unknown>)[key];
                return acc;
            }, {});
        } catch { return String(e); }
    }
    return e;
}

// ─── NATIVE HELPER ──────────────────────────────────────────────────────────

/**
 * Accede al plugin OneSignal nativo tramite il bridge Cordova di Capacitor.
 * Il plugin si registra su window.plugins.OneSignal quando Capacitor carica cordova.js.
 * NON usiamo import() dinamico perché i bare module specifier non risolvono nel WebView.
 */
async function getNativeOneSignal(): Promise<any> {
    // Il plugin si auto-registra su window.plugins.OneSignal via cordova.js
    const w = window as any;
    if (w.plugins?.OneSignal) {
        return w.plugins.OneSignal;
    }

    // Attende che il bridge Cordova carichi il plugin (max ~3s con retry)
    for (let attempt = 0; attempt < 6; attempt++) {
        await new Promise(r => setTimeout(r, 500));
        if (w.plugins?.OneSignal) {
            dbg(`Plugin trovato dopo ${(attempt + 1) * 500}ms`);
            return w.plugins.OneSignal;
        }
    }

    throw new Error(
        'OneSignal native plugin not found on window.plugins.OneSignal. ' +
        'Ensure onesignal-cordova-plugin is in capacitor.config.ts includePlugins and run "npx cap sync".'
    );
}

// ─── WEB HELPERS ────────────────────────────────────────────────────────────

/** Carica lo script CDN di OneSignal v16 */
function loadOneSignalScript(): Promise<void> {
    return new Promise((resolve, reject) => {
        if ((window as any).OneSignal && typeof (window as any).OneSignal.init === 'function') {
            dbg('CDN script già caricato');
            resolve();
            return;
        }

        const existing = document.querySelector(`script[src="${ONESIGNAL_CDN_URL}"]`);
        if (existing) {
            dbg('Script tag già presente, attendo caricamento...');
            existing.addEventListener('load', () => resolve());
            existing.addEventListener('error', (e) => reject(new Error(`CDN script load error: ${e}`)));
            if ((window as any).OneSignal) { resolve(); return; }
            setTimeout(() => reject(new Error('CDN script presente ma non caricato dopo 8s')), 8000);
            return;
        }

        dbg('Iniettando script CDN v16...');
        const script = document.createElement('script');
        script.src = ONESIGNAL_CDN_URL;
        script.async = true;
        script.defer = true;
        script.onload = () => { dbg('CDN script LOADED'); resolve(); };
        script.onerror = (e) => {
            dbg('CDN script LOAD ERROR', e);
            reject(new Error('Impossibile caricare OneSignal CDN (bloccato da ad-blocker o rete?)'));
        };
        document.head.appendChild(script);
    });
}

/** Esegue un'operazione su window.OneSignal con timeout */
function withOS<T>(fn: (os: any) => T | Promise<T>, timeoutMs = 5000): Promise<T> {
    return new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            reject(new Error(`withOS timeout dopo ${timeoutMs}ms`));
        }, timeoutMs);

        try {
            const deferred = (window as any).OneSignalDeferred || (window as any).OneSignal;
            if (Array.isArray(deferred)) {
                deferred.push(async (os: any) => {
                    clearTimeout(timer);
                    try {
                        const result = await fn(os);
                        resolve(result);
                    } catch (e) { reject(e); }
                });
            } else if (deferred && (typeof deferred === 'object' || typeof deferred === 'function')) {
                clearTimeout(timer);
                try {
                    const result = fn(deferred);
                    if (result && typeof (result as { then?: unknown }).then === 'function') {
                        (result as Promise<T>).then(resolve).catch(reject);
                    } else {
                        resolve(result as T);
                    }
                } catch (e) { reject(e); }
            } else {
                clearTimeout(timer);
                reject(new Error('OneSignal non disponibile (né deferred né SDK)'));
            }
        } catch (e) {
            clearTimeout(timer);
            reject(e);
        }
    });
}

/** Attende che il SW OneSignal sia registrato (polling) */
async function waitForSW(maxWaitMs = 8000): Promise<ServiceWorkerRegistration | null> {
    const start = Date.now();
    const interval = 500;
    while (Date.now() - start < maxWaitMs) {
        try {
            const regs = await navigator.serviceWorker.getRegistrations();
            const osReg = regs.find(r =>
                r.active?.scriptURL?.includes('OneSignal') ||
                r.installing?.scriptURL?.includes('OneSignal') ||
                r.waiting?.scriptURL?.includes('OneSignal')
            );
            if (osReg) return osReg;
        } catch { /* ignore */ }
        await new Promise(r => setTimeout(r, interval));
    }
    return null;
}

// ─── MAIN SERVICE ───────────────────────────────────────────────────────────

class OneSignalServiceWrapper {
    private static instance: OneSignalServiceWrapper;
    private initialized = false;
    private initPromise: Promise<void> | null = null;
    private initSuccess = false;
    /** Stores the first init failure so it can be reported after auth is ready */
    private pendingInitError: unknown = null;
    /** Riferimento al modulo nativo OneSignal (solo su piattaforma nativa) */
    private nativeOS: any = null;

    private constructor() { }

    public static getInstance(): OneSignalServiceWrapper {
        if (!OneSignalServiceWrapper.instance) {
            OneSignalServiceWrapper.instance = new OneSignalServiceWrapper();
        }
        return OneSignalServiceWrapper.instance;
    }

    public async init(): Promise<void> {
        if (this.initialized) return;
        if (this.initPromise) return this.initPromise;
        this.initPromise = this._doInit();
        return this.initPromise;
    }

    private async _doInit(): Promise<void> {
        dbg('_doInit START', {
            appId: ONESIGNAL_APP_ID ? `${ONESIGNAL_APP_ID.substring(0, 8)}...` : 'MISSING',
            platform: Capacitor.getPlatform(),
            isNative: this.isNative(),
        });

        try {
            if (!ONESIGNAL_APP_ID) {
                dbg('SKIP: APP_ID mancante');
                this.initialized = true;
                return;
            }

            if (this.isNative()) {
                await this._initNative();
            } else {
                await this._initWeb();
            }
        } catch (error) {
            this.initPromise = null;
            this.initialized = true;
            this.initSuccess = false;
            this.pendingInitError = error;
            dbg('INIT FAILED', serializeError(error));
            // Note: logger may not reach DB here (called before auth) — stored in pendingInitError
            // and re-reported during login() when a session is available.
            logger.notifications.error('Init failed (pre-auth)', serializeError(error), 'oneSignalService');
        }
    }

    // ─── NATIVE INIT (iOS + Android via onesignal-cordova-plugin) ───────

    private async _initNative(): Promise<void> {
        dbg('PATH: Native/Capacitor — usando onesignal-cordova-plugin SDK v5');

        try {
            const OneSignal = await getNativeOneSignal();
            this.nativeOS = OneSignal;

            // Enable verbose logging in debug builds (remove in production)
            if (DEBUG) {
                OneSignal.Debug.setLogLevel(6);
            }

            // Initialize with App ID
            OneSignal.initialize(ONESIGNAL_APP_ID);
            dbg('OneSignal.initialize() chiamato');

            // Set up notification click handler (deep linking)
            // Cordova plugin v5 usa addEventListener, NON addClickListener
            OneSignal.Notifications.addEventListener('click', (event: any) => {
                const data = event?.notification?.additionalData;
                logger.notifications.info('Notification clicked (native)', 'oneSignalService', {
                    title: event?.notification?.title,
                    body: event?.notification?.body,
                    data,
                });

                // Deep link handling — navigate based on notification data
                // Uses globalNavigate() which works with MemoryRouter on native
                // (window.location changes are ignored by MemoryRouter)
                if (data?.url || data?.route) {
                    const route = data.route || data.url;
                    const isExternalUrl = /^https?:\/\//i.test(route);
                    // Per URL esterne: NON aprire il browser, ma portare l'utente
                    // alla pagina notifiche dentro l'app. Il pulsante "Apri" nel
                    // dettaglio si occuperà di lanciare il browser esterno.
                    const normalizedRoute = isExternalUrl
                        ? '/dashboard/notifications'
                        : route.startsWith('/') ? route : `/${route}`;
                    dbg('Deep link from notification', { route: normalizedRoute, isExternalUrl });
                    try {
                        globalNavigate(normalizedRoute, { replace: true });
                    } catch (e) {
                        dbg('Deep link navigation failed', serializeError(e));
                    }
                }
            });

            // Set up foreground notification handler
            // Cordova plugin v5 usa addEventListener('foregroundWillDisplay', fn)
            OneSignal.Notifications.addEventListener('foregroundWillDisplay', (event: any) => {
                dbg('Foreground notification received', {
                    title: event?.notification?.title || event?.getNotification?.()?.title,
                    body: event?.notification?.body || event?.getNotification?.()?.body,
                });
                // Let the notification display (default behavior in Cordova plugin)
            });

            // Set up permission observer
            // Cordova plugin v5 usa addEventListener('permissionChange', fn)
            OneSignal.Notifications.addEventListener('permissionChange', (granted: boolean) => {
                dbg('Permission state changed', { granted });
            });

            // Set up push subscription observer
            // Cordova plugin v5: lowercase 'pushSubscription' (NOT PushSubscription like Web SDK)
            OneSignal.User.pushSubscription.addEventListener('change', (subscription: any) => {
                dbg('PushSubscription changed', {
                    id: subscription?.current?.id,
                    token: subscription?.current?.token,
                    optedIn: subscription?.current?.optedIn,
                });
            });

            this.initialized = true;
            this.initSuccess = true;
            dbg('INIT SUCCESS (native)');

            // Log stato iniziale subscription — visibile in admin panel → app_logs
            try {
                const hasPerm = typeof OneSignal.Notifications.getPermissionAsync === 'function'
                    ? await OneSignal.Notifications.getPermissionAsync()
                    : (OneSignal.Notifications.hasPermission?.() ?? false);
                const optedIn = typeof OneSignal.User.pushSubscription.getOptedInAsync === 'function'
                    ? await OneSignal.User.pushSubscription.getOptedInAsync()
                    : (OneSignal.User.pushSubscription.optedIn ?? false);
                const subId = typeof OneSignal.User.pushSubscription.getIdAsync === 'function'
                    ? await OneSignal.User.pushSubscription.getIdAsync()
                    : (OneSignal.User.pushSubscription.id ?? null);
                logger.notifications.info(
                    'initNative: subscription state at startup',
                    'oneSignalService',
                    { hasPerm, optedIn, subId, hasSubId: !!subId }
                );
            } catch (stateErr) {
                dbg('initNative: stato subscription non leggibile', serializeError(stateErr));
            }

        } catch (err) {
            dbg('Native init FAILED', serializeError(err));
            this.initialized = true;
            this.initSuccess = false;
            this.pendingInitError = err;
            logger.notifications.error('Native init failed', serializeError(err), 'oneSignalService');
        }
    }

    // ─── WEB INIT (Browser via CDN SDK v16) ─────────────────────────────

    private async _initWeb(): Promise<void> {
        dbg('PATH: Web/Browser', {
            swSupport: 'serviceWorker' in navigator,
            notificationApi: 'Notification' in window,
            currentPermission: 'Notification' in window ? Notification.permission : 'N/A',
            protocol: window.location.protocol,
            host: window.location.host,
        });

        if (!('serviceWorker' in navigator)) {
            dbg('SKIP: Service Workers non supportati');
            this.initialized = true;
            return;
        }

        if (!('Notification' in window)) {
            dbg('SKIP: Notification API non disponibile');
            this.initialized = true;
            return;
        }

        // 1. Setup deferred queue PRIMA di caricare lo script
        (window as any).OneSignalDeferred = (window as any).OneSignalDeferred || [];
        dbg('OneSignalDeferred array pronto');

        // 2. Carica il CDN script
        try {
            await Promise.race([
                loadOneSignalScript(),
                new Promise<void>((_, reject) =>
                    setTimeout(() => reject(new Error('CDN script timeout dopo 10s')), 10000)
                )
            ]);
        } catch (scriptErr) {
            dbg('CDN script FAILED', serializeError(scriptErr));
            this.initialized = true;
            this.initSuccess = false;
            return;
        }

        // 3. Verifica che il file SW sia accessibile
        try {
            const swResp = await fetch('/OneSignalSDKWorker.js', { method: 'HEAD' });
            dbg('SW file check', { status: swResp.status, ok: swResp.ok, contentType: swResp.headers.get('content-type') });
            if (!swResp.ok) {
                dbg('⚠️ SW file NON accessibile! Status:', swResp.status);
            }
        } catch (e) { dbg('SW file fetch FAILED', serializeError(e)); }

        // 4. Inizializza via deferred queue
        dbg('Pushing init config to OneSignalDeferred...');
        await withOS(async (OneSignalSDK) => {
            dbg('Inside deferred callback — calling SDK.init()');
            await OneSignalSDK.init({
                appId: ONESIGNAL_APP_ID,
                allowLocalhostAsSecureOrigin: true,
            });
        }, 15000);

        dbg('SDK init() COMPLETED');

        // 5. Verifica post-init
        dbg('Post-init state', {
            'window.OneSignal': typeof (window as any).OneSignal,
            'OneSignal.User': typeof (window as any).OneSignal?.User,
        });

        // 6. Attendi registrazione SW
        dbg('Attendo registrazione SW (auto dal SDK)...');
        const swReg = await waitForSW(8000);
        if (swReg) {
            dbg('SW registrato!', {
                scope: swReg.scope,
                active: swReg.active?.scriptURL,
                installing: swReg.installing?.scriptURL,
                waiting: swReg.waiting?.scriptURL,
            });
        } else {
            dbg('⚠️ SW NON registrato dopo 8s — push potrebbe non funzionare');
        }

        // 7. Verifica finale
        try {
            const regs = await navigator.serviceWorker.getRegistrations();
            dbg('ServiceWorker registrations (finale)', regs.map(r => ({
                scope: r.scope,
                active: r.active?.scriptURL,
                installing: r.installing?.scriptURL,
            })));
        } catch (e) { dbg('SW getRegistrations failed', serializeError(e)); }

        try {
            const os = (window as any).OneSignal;
            dbg('PushSubscription state', {
                id: os?.User?.PushSubscription?.id ?? null,
                token: os?.User?.PushSubscription?.token ?? null,
                optedIn: os?.User?.PushSubscription?.optedIn ?? false,
            });
        } catch (e) { dbg('PushSubscription read failed', serializeError(e)); }

        this.initialized = true;
        this.initSuccess = true;
        dbg('INIT SUCCESS');
    }

    // ─── PUBLIC API ─────────────────────────────────────────────────────

    private get os(): any {
        return (window as any).OneSignal;
    }

    public async login(userId: string) {
        if (!this.initialized) await this.init();

        // If init failed on first attempt (e.g. Cordova bridge not yet ready at cold-start),
        // retry once here — login() is always called after auth, giving the bridge time to load.
        if (!this.initSuccess) {
            // Now we have a valid session — report the stored pre-auth init error to DB
            if (this.pendingInitError) {
                logger.notifications.error(
                    'Init failed on startup (reported post-auth)',
                    this.pendingInitError,
                    'oneSignalService',
                    { userId }
                );
                this.pendingInitError = null;
            }
            logger.notifications.warn(
                'login: init failed on startup — retrying now (post-auth)',
                'oneSignalService',
                { userId }
            );
            this.initialized = false;
            this.initPromise = null;
            await this.init();
        }

        if (!this.initSuccess) {
            logger.notifications.warn(
                'login SKIP — init failed after retry',
                'oneSignalService',
                { userId }
            );
            // Persist init failure to user_devices so admin diagnostics can see it
            if (this.isNative()) {
                const platform = Capacitor.getPlatform() as 'ios' | 'android';
                const errorMsg = this.pendingInitError instanceof Error
                    ? this.pendingInitError.message
                    : String(this.pendingInitError ?? 'unknown init error');
                supabase.from('user_devices').upsert({
                    user_id: userId,
                    platform,
                    push_permission: false,
                    opted_in: false,
                    init_error: errorMsg.substring(0, 500),
                    last_seen_at: new Date().toISOString(),
                }, { onConflict: 'user_id,platform' }).then(({ error }) => {
                    if (error) dbg('user_devices upsert (init fail) error', serializeError(error));
                });
            }
            return;
        }

        try {
            if (this.isNative()) {
                if (this.nativeOS) {
                    // login() deve completare prima di optIn() per associare il token all'utente corretto.
                    // Timeout di 8s: su iOS al cold start il callback Cordova può non tornare mai —
                    // in quel caso procediamo comunque con l'enforce (la subscription è già valida,
                    // solo l'associazione external_id potrebbe essere ritardata lato OneSignal).
                    const loginTimeout = new Promise<void>(resolve => setTimeout(resolve, 8000));
                    await Promise.race([this.nativeOS.login(userId), loginTimeout]);
                    dbg('login SUCCESS (native)', userId);

                    // Enforce subscription state dopo login (handles post-update resets)
                    await this._enforceNativeSubscription(userId, 'login');
                }
            } else {
                if (!this.os) { dbg('login SKIP — no web SDK'); return; }
                // Web: retry con backoff
                const maxRetries = 3;
                for (let i = 0; i < maxRetries; i++) {
                    try {
                        dbg(`login() attempt ${i + 1}/${maxRetries}`, userId);
                        await this.os.login(userId);
                        dbg('login SUCCESS', userId);
                        return;
                    } catch (e) {
                        dbg(`login attempt ${i + 1} FAILED`, serializeError(e));
                        if (i < maxRetries - 1) {
                            const delay = 2000 * (i + 1);
                            dbg(`Retry login tra ${delay}ms...`);
                            await new Promise(r => setTimeout(r, delay));
                        }
                    }
                }
                dbg('login FAILED dopo tutti i tentativi');
            }
        } catch (e) {
            dbg('login FAILED', serializeError(e));
        }
    }

    /**
     * Verifica e ripristina lo stato della subscription nativa.
     * Chiamato dopo login() e al ritorno in foreground.
     *
     * Garantisce che:
     * - Se il permesso è concesso → optedIn=true e subId valido
     * - I tag di default (bookings, system) siano sempre presenti
     * - Ogni anomalia venga loggata su DB per diagnostica
     */
    private async _enforceNativeSubscription(userId: string, caller: string): Promise<void> {
        if (!this.nativeOS) return;

        try {
            // Letture in parallelo con timeout tri-state: il bridge Cordova al cold start
            // post-update a volte non risponde affatto (bug noto, bibbia §3.0). Un timeout
            // NON è mai trattato come "false" — è il bug che questa patch risolve.
            const [permissionRead, optedInRead, subIdRead, subTokenRead] = await Promise.all([
                typeof this.nativeOS.Notifications.getPermissionAsync === 'function'
                    ? readWithTimeout(this.nativeOS.Notifications.getPermissionAsync())
                    : Promise.resolve(this.nativeOS.Notifications.hasPermission?.() ?? false),
                typeof this.nativeOS.User.pushSubscription.getOptedInAsync === 'function'
                    ? readWithTimeout(this.nativeOS.User.pushSubscription.getOptedInAsync())
                    : Promise.resolve(this.nativeOS.User.pushSubscription.optedIn ?? false),
                typeof this.nativeOS.User.pushSubscription.getIdAsync === 'function'
                    ? readWithTimeout(this.nativeOS.User.pushSubscription.getIdAsync())
                    : Promise.resolve(this.nativeOS.User.pushSubscription.id ?? null),
                typeof this.nativeOS.User.pushSubscription.getTokenAsync === 'function'
                    ? readWithTimeout(this.nativeOS.User.pushSubscription.getTokenAsync())
                    : Promise.resolve(this.nativeOS.User.pushSubscription.token ?? null),
            ]);

            const permissionUnknown = permissionRead === READ_TIMEOUT;
            const optedInUnknown = optedInRead === READ_TIMEOUT;
            const subIdUnknown = subIdRead === READ_TIMEOUT;

            // Permesso: se la lettura riesce è la verità (anche se negativa — un rifiuto REALE
            // va rispettato) e aggiorna il flag persistito. Se va in timeout, il flag persistito
            // (ultima lettura riuscita, sopravvive a update/reinstall) è la nostra unica fonte —
            // mai un default "false" che salterebbe la riparazione proprio quando serve.
            let hasPermission: boolean;
            if (!permissionUnknown) {
                hasPermission = !!permissionRead;
                storage.set(PUSH_GRANTED_KEY, hasPermission ? '1' : '0').catch(() => {});
            } else {
                hasPermission = (await storage.get(PUSH_GRANTED_KEY)) === '1';
            }

            // optedIn/subId sconosciuti → assumiamo lo scenario peggiore (non opted-in / nessun
            // subId): se in realtà lo erano già, l'optIn() più sotto è un no-op innocuo lato
            // OneSignal (non mostra alcun popup di sistema — a differenza di requestPermission()).
            const optedIn = optedInUnknown ? false : !!optedInRead;
            const subId = subIdUnknown ? null : (subIdRead as string | null);
            const subToken = subTokenRead === READ_TIMEOUT ? null : (subTokenRead as string | null);

            logger.notifications.info(
                `${caller}: subscription state`,
                'oneSignalService',
                { userId, hasPermission, optedIn, subId, hasToken: !!subToken, permissionUnknown, optedInUnknown, subIdUnknown }
            );

            // Case 1: permission granted but not opted in (common after app update)
            // Case 2: opted in but no subId (subscription corrupted — re-opt-in)
            const needsOptIn = hasPermission && (!optedIn || !subId);
            // subIdAfter: declared here so it's accessible for the upsert below
            let subIdAfter: string | null = null;
            if (needsOptIn) {
                logger.notifications.info(
                    `${caller}: forzo optIn() — hasPermission=${hasPermission} optedIn=${optedIn} subId=${subId}`,
                    'oneSignalService',
                    { userId }
                );
                await this.nativeOS.User.pushSubscription.optIn();
                subIdAfter = typeof this.nativeOS.User.pushSubscription.getIdAsync === 'function'
                    ? await this.nativeOS.User.pushSubscription.getIdAsync()
                    : (this.nativeOS.User.pushSubscription.id ?? null);
                logger.notifications.info(
                    `${caller}: optIn() completato`,
                    'oneSignalService',
                    { userId, subIdBefore: subId, subIdAfter, success: !!subIdAfter }
                );
            }

            // Always ensure default tags — they can be lost after updates/reinstall
            if (hasPermission && (optedIn || needsOptIn)) {
                await this.ensureDefaultTags();
            }

            // Non-blocking upsert to user_devices — diagnostics only, never blocks push flow
            const platform = Capacitor.getPlatform() as 'ios' | 'android';
            let appVersion: string | null = null;
            try {
                const { App } = await import('@capacitor/app');
                const info = await App.getInfo();
                appVersion = `${info.version} (${info.build})`;
            } catch { /* non-blocking */ }

            // Usa subIdAfter (già letto dopo optIn) — evita una terza chiamata a getIdAsync
            const finalOptedIn = needsOptIn ? true : optedIn;
            const finalSubId = needsOptIn ? subIdAfter : subId;

            supabase.from('user_devices').upsert({
                user_id: userId,
                platform,
                app_version: appVersion,
                onesignal_subscription_id: finalSubId ?? null,
                push_permission: hasPermission,
                opted_in: finalOptedIn,
                state: {
                    caller,
                    hasPermission,
                    optedIn,
                    subId,
                    needsOptIn,
                    ts: new Date().toISOString(),
                },
                init_error: null,
                last_seen_at: new Date().toISOString(),
            }, { onConflict: 'user_id,platform' }).then(({ error }) => {
                if (error) {
                    logger.notifications.warn(
                        `${caller}: user_devices upsert failed (non-blocking)`,
                        'oneSignalService',
                        { userId, error: serializeError(error) }
                    );
                }
            });

        } catch (err) {
            logger.notifications.warn(
                `${caller}: _enforceNativeSubscription failed (non-blocking)`,
                'oneSignalService',
                { userId, error: serializeError(err) }
            );
        }
    }

    /** Timestamp dell'ultima riparazione lanciata dal ritorno in foreground (throttle). */
    private lastForegroundCheckAt = 0;

    /**
     * Ri-verifica e ripara lo stato della subscription al ritorno dell'app in foreground.
     * Prima di questa patch l'enforce girava SOLO dopo login() — cioè quasi mai, dato che
     * su un device già autenticato il cold start post-update spesso non richiama login().
     * Throttled a ~15 minuti per non martellare il bridge nativo a ogni switch di tab.
     * Va chiamato da un componente sempre montato (vedi AuthContext + useAppFocus).
     */
    public async recheckOnForeground(userId: string): Promise<void> {
        if (!this.isNative() || !this.nativeOS || !userId) return;
        const now = Date.now();
        if (now - this.lastForegroundCheckAt < 15 * 60 * 1000) return;
        this.lastForegroundCheckAt = now;
        await this._enforceNativeSubscription(userId, 'foreground');
    }

    public async logout() {
        if (!this.initSuccess) return;

        try {
            if (this.isNative()) {
                if (this.nativeOS) {
                    this.nativeOS.logout();
                    dbg('logout SUCCESS (native)');
                }
            } else {
                if (this.os) {
                    await this.os.logout();
                    dbg('logout SUCCESS (web)');
                }
            }
        } catch (e) {
            dbg('logout FAILED', serializeError(e));
        }
    }

    public async requestPermission(): Promise<boolean> {
        if (!this.initialized) await this.init();
        if (!this.initSuccess) { dbg('requestPermission SKIP'); return false; }

        try {
            if (this.isNative()) {
                if (!this.nativeOS) return false;
                const accepted = await this.nativeOS.Notifications.requestPermission(true);
                dbg('requestPermission result (native)', { accepted });
                return !!accepted;
            } else {
                if (!this.os) return false;
                dbg('requestPermission: stato attuale', {
                    currentPermission: Notification.permission,
                    slidedownAvailable: !!this.os?.Slidedown,
                    notificationsAvailable: !!this.os?.Notifications,
                });

                if (Notification.permission === 'default') {
                    try {
                        dbg('Tentativo OneSignal.Notifications.requestPermission()...');
                        await this.os.Notifications.requestPermission();
                    } catch (notifErr) {
                        dbg('OneSignal.Notifications.requestPermission() failed', serializeError(notifErr));
                        try {
                            dbg('Fallback a Notification.requestPermission() nativo...');
                            await Notification.requestPermission();
                        } catch (nativeErr) {
                            dbg('Notification.requestPermission() nativo failed', serializeError(nativeErr));
                        }
                    }
                }

                const result = Notification.permission === 'granted';
                dbg('requestPermission result', { permission: Notification.permission, result });

                if (result) {
                    try {
                        await this.os.User.PushSubscription.optIn();
                        dbg('optIn dopo requestPermission SUCCESS');
                        await new Promise(r => setTimeout(r, 1000));
                        const subState = {
                            id: this.os.User?.PushSubscription?.id ?? null,
                            token: this.os.User?.PushSubscription?.token ?? null,
                            optedIn: this.os.User?.PushSubscription?.optedIn ?? false,
                        };
                        dbg('Subscription dopo optIn', subState);
                    } catch (e) { dbg('optIn dopo requestPermission FAILED', serializeError(e)); }
                }

                return result;
            }
        } catch (e) {
            dbg('requestPermission FAILED', serializeError(e));
            return false;
        }
    }

    public async sendTag(key: string, value: string) {
        if (!this.initSuccess) return;
        try {
            if (this.isNative()) {
                if (this.nativeOS) {
                    this.nativeOS.User.addTag(key, value);
                    dbg(`Tag set (native): ${key}=${value}`);
                }
            } else {
                if (this.os) {
                    this.os.User.addTag(key, value);
                    dbg(`Tag set: ${key}=${value}`);
                }
            }
        } catch (e) { dbg('sendTag FAILED', serializeError(e)); }
    }

    public async sendTags(tags: Record<string, string>) {
        if (!this.initSuccess) return;
        try {
            if (this.isNative()) {
                if (this.nativeOS) {
                    this.nativeOS.User.addTags(tags);
                    dbg('Tags set (native)', tags);
                }
            } else {
                if (this.os) {
                    this.os.User.addTags(tags);
                    dbg('Tags set', tags);
                }
            }
        } catch (e) { dbg('sendTags FAILED', serializeError(e)); }
    }

    public async getTags(): Promise<Record<string, string> | undefined> {
        if (!this.initSuccess) return undefined;
        try {
            if (this.isNative()) {
                if (this.nativeOS) {
                    const tags = this.nativeOS.User.getTags();
                    dbg('getTags (native)', tags);
                    return tags;
                }
                return undefined;
            }
            if (this.os) {
                const tags = this.os.User.getTags();
                dbg('getTags', tags);
                return tags;
            }
            return undefined;
        } catch (e) {
            dbg('getTags FAILED', serializeError(e));
            return undefined;
        }
    }

    public async getSubscriptionState(): Promise<{
        id: string | null;
        token: string | null;
        optedIn: boolean;
    }> {
        if (!this.initialized) await this.init();
        if (!this.initSuccess) {
            return { id: null, token: null, optedIn: false };
        }

        try {
            if (this.isNative()) {
                if (this.nativeOS) {
                    const sub = this.nativeOS.User.pushSubscription;
                    // Cordova plugin v5: usa metodi async invece di proprietà deprecate.
                    // Timeout-safe: un bridge che non risponde in tempo ricade sui default
                    // "nessuna subscription" invece di propagare un'eccezione o un dato stantio.
                    const idRead = (typeof sub?.getIdAsync === 'function') ? await readWithTimeout(sub.getIdAsync()) : (sub?.id ?? null);
                    const tokenRead = (typeof sub?.getTokenAsync === 'function') ? await readWithTimeout(sub.getTokenAsync()) : (sub?.token ?? null);
                    const optedInRead = (typeof sub?.getOptedInAsync === 'function') ? await readWithTimeout(sub.getOptedInAsync()) : (sub?.optedIn ?? false);
                    const state = {
                        id: idRead === READ_TIMEOUT ? null : (idRead as string | null),
                        token: tokenRead === READ_TIMEOUT ? null : (tokenRead as string | null),
                        optedIn: optedInRead === READ_TIMEOUT ? false : !!optedInRead,
                    };
                    dbg('getSubscriptionState (native)', state);
                    return state;
                }
                return { id: null, token: null, optedIn: false };
            }
            if (this.os) {
                const state = {
                    id: this.os.User?.PushSubscription?.id ?? null,
                    token: this.os.User?.PushSubscription?.token ?? null,
                    optedIn: this.os.User?.PushSubscription?.optedIn ?? false,
                };
                dbg('getSubscriptionState', state);
                return state;
            }
            return { id: null, token: null, optedIn: false };
        } catch (e) {
            dbg('getSubscriptionState FAILED', serializeError(e));
            return { id: null, token: null, optedIn: false };
        }
    }

    public async optIn(): Promise<void> {
        if (!this.initSuccess) return;
        try {
            if (this.isNative()) {
                if (this.nativeOS) {
                    this.nativeOS.User.pushSubscription.optIn();
                    dbg('optIn SUCCESS (native)');
                }
            } else {
                if (this.os) {
                    await this.os.User.PushSubscription.optIn();
                    dbg('optIn SUCCESS');
                }
            }
            // Set default tags so OneSignal tag-based filters work for new users
            await this.ensureDefaultTags();
        } catch (e) { dbg('optIn FAILED', serializeError(e)); }
    }

    /**
     * Ensures default notification category tags exist.
     * New users who opt-in need these tags so that admin push notifications
     * with tag filters (e.g. system != 0) reach them.
     */
    private async ensureDefaultTags(): Promise<void> {
        try {
            const existing = await this.getTags();
            const defaults: Record<string, string> = {};
            if (!existing?.bookings) defaults.bookings = '1';
            if (!existing?.system) defaults.system = '1';
            if (Object.keys(defaults).length > 0) {
                await this.sendTags(defaults);
                dbg('Default tags set', defaults);
            }
        } catch (e) { dbg('ensureDefaultTags FAILED', serializeError(e)); }
    }

    public async optOut(): Promise<void> {
        if (!this.initSuccess) return;
        try {
            if (this.isNative()) {
                if (this.nativeOS) {
                    this.nativeOS.User.pushSubscription.optOut();
                    dbg('optOut SUCCESS (native)');
                }
            } else {
                if (this.os) {
                    dbg('optOut()');
                    await this.os.User.PushSubscription.optOut();
                    dbg('optOut SUCCESS');
                }
            }
        } catch (e) { dbg('optOut FAILED', serializeError(e)); }
    }

    /**
     * Restituisce true se il permesso push è già stato concesso su nativo.
     * Su web, usa Notification.permission.
     * Non mostra nessun prompt.
     */
    public async hasPermission(): Promise<boolean> {
        if (!this.initialized) await this.init();
        if (!this.initSuccess) return false;

        try {
            if (this.isNative()) {
                if (!this.nativeOS) return false;
                // Cordova plugin v5: hasPermission() restituisce boolean sync,
                // getPermissionAsync() restituisce Promise<boolean> — quest'ultima può non
                // rispondere affatto al cold start post-update (bridge non pronto).
                const permissionRead = typeof this.nativeOS.Notifications.getPermissionAsync === 'function'
                    ? await readWithTimeout(this.nativeOS.Notifications.getPermissionAsync())
                    : (this.nativeOS.Notifications.hasPermission?.() ?? false);

                if (permissionRead === READ_TIMEOUT) {
                    // Non sappiamo la verità adesso: ci fidiamo dell'ultima lettura riuscita,
                    // MAI di un default "negato" (quello è il bug che facevamo prima).
                    const stored = await storage.get(PUSH_GRANTED_KEY);
                    dbg('hasPermission: lettura in timeout, uso flag persistito', { stored });
                    return stored === '1';
                }

                const granted = !!permissionRead;
                storage.set(PUSH_GRANTED_KEY, granted ? '1' : '0').catch(() => {});
                dbg('hasPermission (native)', { granted });
                return granted;
            } else {
                return Notification.permission === 'granted';
            }
        } catch (e) {
            dbg('hasPermission FAILED', serializeError(e));
            return false;
        }
    }

    /**
     * Chiede il permesso push SOLO SE non è ancora stato concesso o rifiutato.
     * Ideale per il prompt contestuale una-tantum nell'area cliente.
     * Restituisce true se il permesso è stato concesso (ora o in precedenza).
     */
    public async requestPermissionIfNeeded(): Promise<boolean> {
        if (!this.initialized) await this.init();
        if (!this.initSuccess) { dbg('requestPermissionIfNeeded SKIP — not initialized'); return false; }

        try {
            if (this.isNative()) {
                if (!this.nativeOS) return false;

                // Controlla se il permesso è già stato concesso
                let alreadyGranted = false;
                if (typeof this.nativeOS.Notifications.getPermissionAsync === 'function') {
                    alreadyGranted = await this.nativeOS.Notifications.getPermissionAsync();
                } else {
                    alreadyGranted = this.nativeOS.Notifications.hasPermission?.() ?? false;
                }
                if (alreadyGranted) {
                    dbg('requestPermissionIfNeeded: già concesso, skip');
                    return true;
                }

                // Chiede il permesso (con fallbackToSettings=true)
                dbg('requestPermissionIfNeeded: chiedo permesso nativo...');
                const accepted = await this.nativeOS.Notifications.requestPermission(true);
                dbg('requestPermissionIfNeeded result', { accepted });
                return !!accepted;
            } else {
                // Web: controlla stato corrente
                if (!('Notification' in window)) return false;
                if (Notification.permission === 'granted') return true;
                if (Notification.permission === 'denied') return false;
                // 'default' → chiedi
                return this.requestPermission();
            }
        } catch (e) {
            dbg('requestPermissionIfNeeded FAILED', serializeError(e));
            return false;
        }
    }

    private isNative(): boolean {
        return Capacitor.isNativePlatform();
    }
}

export const oneSignalService = OneSignalServiceWrapper.getInstance();
